import { useEffect, useId, useRef, type ReactNode } from "react";
import { defineStrings, useStrings } from "../i18n";
import { Icon } from "./Icon";

const strings = defineStrings({ close: "Close" }, { close: "Fermer" });

/**
 * A modal sheet: from the bottom on narrow screens, from the side on wide ones. Built on
 * <dialog>, so focus is trapped, Escape closes and the page behind is inert natively.
 * Focus returns to the control that opened it.
 */
export function Sheet(props: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const t = useStrings(strings);
  const id = useId();
  const { open, onClose } = props;

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (open && !dialog.open) {
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
    } else if (!open && dialog.open) {
      if (typeof dialog.close === "function") dialog.close();
      else dialog.removeAttribute("open");
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="ui-sheet"
      aria-labelledby={`${id}-title`}
      onClose={onClose}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // A click on the backdrop (the dialog element itself, outside the panel) closes.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="ui-sheet__panel">
        <header className="ui-sheet__header">
          <h2 id={`${id}-title`}>{props.title}</h2>
          <button
            type="button"
            className="ui-icon-button"
            onClick={onClose}
            aria-label={t("close")}
          >
            <Icon name="close" />
          </button>
        </header>
        <div className="ui-sheet__body">{props.children}</div>
      </div>
    </dialog>
  );
}
